/* Notification Center — in-memory demo store (Zustand).
 *
 * The module has no backend yet (AI-only design module, like Performance
 * Analytics).  This store holds the editable demo data so the toolbar actions
 * (New / Edit / Delete / toggle) actually mutate state and persist across the
 * sub-page navigations (AppShell remounts the page per nav id).  When the real
 * API lands this is replaced by reader/mutation hooks. */
import { create } from 'zustand';
import {
  NC_CATEGORIES, NC_RECIPIENT_GROUPS, NC_TEMPLATES, NC_DELIVERIES,
  type NcCategory, type NcRule, type NcRecipientGroup, type NcTemplate, type NcDelivery,
} from './data';

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export interface NcSummary {
  totalRules: number; activeRules: number; disabledRules: number;
  requireAck: number; outbound: number; needRecipients: number;
  categories: number; activeCategories: number;
  recipientGroups: number; templates: number;
  deliveries24h: number; deliveryFailures: number;
}

export function summaryOf(categories: NcCategory[], recipients: NcRecipientGroup[], templates: NcTemplate[]): NcSummary {
  const rules = categories.flatMap((c) => c.rules);
  return {
    totalRules: rules.length,
    activeRules: rules.filter((r) => r.enabled).length,
    disabledRules: rules.filter((r) => !r.enabled).length,
    requireAck: rules.filter((r) => r.ack).length,
    outbound: rules.filter((r) => r.outbound).length,
    needRecipients: 6, // demo metric (MDI defaults shipped without recipients) — matches the HTML prototype
    categories: categories.length,
    activeCategories: categories.filter((c) => c.active).length,
    recipientGroups: recipients.length,
    templates: templates.length,
    deliveries24h: 524,
    deliveryFailures: 6,
  };
}

interface NcStore {
  categories: NcCategory[];
  recipients: NcRecipientGroup[];
  templates: NcTemplate[];
  deliveries: NcDelivery[];

  // rules
  addRule: (categoryName: string, rule: NcRule) => void;
  deleteRuleCodes: (codes: string[]) => void;
  toggleRule: (code: string) => void;

  // categories
  addCategory: (cat: NcCategory) => void;
  updateCategory: (code: number, patch: Partial<NcCategory>) => void;
  deleteCategoryCodes: (codes: number[]) => void;

  // recipient groups
  addRecipient: (group: NcRecipientGroup) => void;
  updateRecipient: (name: string, patch: Partial<NcRecipientGroup>) => void;
  deleteRecipientNames: (names: string[]) => void;

  // email templates
  addTemplate: (tpl: NcTemplate) => void;
  updateTemplate: (name: string, patch: Partial<NcTemplate>) => void;
  deleteTemplateNames: (names: string[]) => void;

  reset: () => void;
}

export const useNcStore = create<NcStore>((set) => ({
  categories: clone(NC_CATEGORIES),
  recipients: clone(NC_RECIPIENT_GROUPS),
  templates: clone(NC_TEMPLATES),
  deliveries: clone(NC_DELIVERIES),

  addRule: (categoryName, rule) =>
    set((s) => ({
      categories: s.categories.map((c) =>
        c.name === categoryName ? { ...c, rules: [...c.rules, rule] } : c),
    })),
  deleteRuleCodes: (codes) =>
    set((s) => ({
      categories: s.categories.map((c) => ({ ...c, rules: c.rules.filter((r) => !codes.includes(r.code)) })),
    })),
  toggleRule: (code) =>
    set((s) => ({
      categories: s.categories.map((c) => ({
        ...c, rules: c.rules.map((r) => (r.code === code ? { ...r, enabled: !r.enabled } : r)),
      })),
    })),

  addCategory: (cat) => set((s) => ({ categories: [...s.categories, cat] })),
  updateCategory: (code, patch) =>
    set((s) => ({ categories: s.categories.map((c) => (c.code === code ? { ...c, ...patch } : c)) })),
  deleteCategoryCodes: (codes) =>
    set((s) => ({ categories: s.categories.filter((c) => !codes.includes(c.code)) })),

  addRecipient: (group) => set((s) => ({ recipients: [...s.recipients, group] })),
  updateRecipient: (name, patch) =>
    set((s) => ({ recipients: s.recipients.map((r) => (r.name === name ? { ...r, ...patch } : r)) })),
  deleteRecipientNames: (names) =>
    set((s) => ({ recipients: s.recipients.filter((r) => !names.includes(r.name)) })),

  addTemplate: (tpl) => set((s) => ({ templates: [...s.templates, tpl] })),
  updateTemplate: (name, patch) =>
    set((s) => ({ templates: s.templates.map((t) => (t.name === name ? { ...t, ...patch } : t)) })),
  deleteTemplateNames: (names) =>
    set((s) => ({ templates: s.templates.filter((t) => !names.includes(t.name)) })),

  reset: () => set({
    categories: clone(NC_CATEGORIES),
    recipients: clone(NC_RECIPIENT_GROUPS),
    templates: clone(NC_TEMPLATES),
    deliveries: clone(NC_DELIVERIES),
  }),
}));
